// src/pages/CreatorPage.jsx — Marketplace management dashboard
import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import NotificationBell from '../components/NotificationBell';

function StatCard({ label, value, sub, color = 'var(--accent)', icon }) {
  return (
    <div style={{ background:'var(--surface)', border:'0.5px solid var(--border)', borderRadius:12, padding:'14px 16px', display:'flex', flexDirection:'column', gap:3 }}>
      <div style={{ fontSize:10, color:'var(--dim)', textTransform:'uppercase', letterSpacing:'0.06em', display:'flex', alignItems:'center', gap:5 }}>
        {icon && <span>{icon}</span>}{label}
      </div>
      <div style={{ fontSize:24, fontWeight:700, color, letterSpacing:'-0.02em', lineHeight:1.1 }}>{value}</div>
      {sub && <div style={{ fontSize:11, color:'var(--dim)' }}>{sub}</div>}
    </div>
  );
}


// ── Enforce square aspect ratio on image upload ─────────────────────────────
// Returns a square-cropped Blob (center crop) from any image file.
// This ensures all marketplace covers are 1:1 regardless of what's uploaded.
async function cropToSquare(file, maxSize = 1080) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const size = Math.min(img.width, img.height, maxSize);
      const canvas = document.createElement('canvas');
      canvas.width  = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      // Centre-crop
      const sx = (img.width  - size) / 2;
      const sy = (img.height - size) / 2;
      ctx.drawImage(img, sx, sy, size, size, 0, 0, size, size);
      canvas.toBlob(blob => {
        if (blob) resolve(blob);
        else reject(new Error('Canvas to blob failed'));
      }, 'image/jpeg', 0.92);
    };
    img.onerror = () => reject(new Error('Image load failed'));
    img.src = url;
  });
}
function EditListingModal({ listing, onClose, onSaved }) {
  const { user } = useAuth();
  const [form, setForm] = useState({
    title:       listing.title       || '',
    description: listing.description || '',
    tags:        (listing.tags || []).join(', '),
    price_cents: listing.price_cents || 0,
    cover_color: listing.cover_color || '#7c6ffb',
  });
  const [coverUrl, setCoverUrl]   = useState(listing.cover_image_url || '');
  const [uploading, setUploading] = useState(false);
  const [saving,    setSaving]    = useState(false);
  const setV = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const COVER_COLORS = ['#7c6ffb','#1d9e75','#ef4444','#f59e0b','#3b82f6','#8b5cf6','#ec4899','#06b6d4'];

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    if (file.size > 10 * 1024 * 1024) { alert('Image must be under 10MB'); return; }
    setUploading(true);
    try {
      // Crop to square (1080×1080 max) before uploading
      const cropped = await cropToSquare(file, 1080);
      const { supabase } = await import('../lib/supabase');
      const path = `marketplace-covers/${user.id}-${Date.now()}.jpg`;
      const { error } = await supabase.storage.from('entry-files').upload(path, cropped, { upsert: true, contentType: 'image/jpeg' });
      if (error) throw error;
      const { data } = supabase.storage.from('entry-files').getPublicUrl(path);
      setCoverUrl(data.publicUrl);
    } catch (err) { alert('Upload failed: ' + err.message); }
    setUploading(false);
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.description.trim()) { alert('Title and description required.'); return; }
    setSaving(true);
    try {
      const { supabase } = await import('../lib/supabase');
      const { error } = await supabase.from('marketplace_listings').update({
        title:           form.title.trim(),
        description:     form.description.trim(),
        tags:            form.tags.split(',').map(t => t.trim()).filter(Boolean),
        price_cents:     Math.max(0, parseInt(form.price_cents) || 0),
        cover_color:     form.cover_color,
        cover_image_url: coverUrl || null,
      }).eq('id', listing.listing_id).eq('user_id', user.id);
      if (error) throw error;

      // Sync cover image back to the source skill or playbook row
      // so it shows on My Skills / My Playbooks cards too
      if (coverUrl && listing.item_id) {
        const table = listing.item_type === 'playbook' ? 'skill_packs' : 'skills';
        const field = listing.item_type === 'playbook' ? 'cover_image_url' : 'output_image_url';
        await supabase.from(table).update({ [field]: coverUrl }).eq('id', listing.item_id).eq('user_id', user.id);
      }

      onSaved({ ...listing, ...form, cover_image_url: coverUrl, tags: form.tags.split(',').map(t=>t.trim()).filter(Boolean) });
    } catch (err) { alert('Save failed: ' + err.message); }
    setSaving(false);
  };

  return (
    <div style={{ position:'fixed', inset:0, zIndex:10200, background:'rgba(0,0,0,0.6)', backdropFilter:'blur(4px)', display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background:'var(--surface)', borderRadius:16, width:'100%', maxWidth:520, border:'0.5px solid var(--border2)', maxHeight:'92vh', overflowY:'auto' }}>
        <div style={{ padding:'18px 20px 0', display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
          <div>
            <div style={{ fontSize:15, fontWeight:600, color:'var(--text)' }}>Edit listing</div>
            <div style={{ fontSize:11, color:'var(--dim)', marginTop:2 }}>{listing.item_type === 'playbook' ? '📦' : '⚡'} {listing.title}</div>
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--dim)', fontSize:20 }}>×</button>
        </div>
        <div style={{ padding:'0 20px 20px', display:'flex', flexDirection:'column', gap:14 }}>
          <div>
            <label style={s.label}>Title *</label>
            <input style={s.input} value={form.title} onChange={e => setV('title', e.target.value)} />
          </div>
          <div>
            <label style={s.label}>Description *</label>
            <textarea style={{ ...s.input, minHeight:88, resize:'vertical', lineHeight:1.6 }}
              placeholder="What does this do? Who is it for?"
              value={form.description} onChange={e => setV('description', e.target.value)} />
          </div>
          <div>
            <label style={s.label}>Tags <span style={{ color:'var(--dim)', fontWeight:400 }}>(comma-separated)</span></label>
            <input style={s.input} placeholder="writing, email, marketing" value={form.tags} onChange={e => setV('tags', e.target.value)} />
          </div>
          <div>
            <label style={s.label}>Price</label>
            <div style={{ display:'flex', gap:6, alignItems:'center', flexWrap:'wrap' }}>
              {[0,100,200,300,500,1000].map(cents => (
                <button key={cents} onClick={() => setV('price_cents', cents)} style={{
                  padding:'5px 11px', borderRadius:7, border:'0.5px solid', fontFamily:'var(--font)', fontSize:12, cursor:'pointer',
                  background: form.price_cents === cents ? 'var(--accent-dim)' : 'none',
                  color:      form.price_cents === cents ? 'var(--accent)'     : 'var(--muted)',
                  borderColor: form.price_cents === cents ? 'var(--accent-border)' : 'var(--border2)',
                }}>
                  {cents === 0 ? 'Free' : `$${(cents/100).toFixed(2)}`}
                </button>
              ))}
              <span style={{ fontSize:10, color:'var(--dim)' }}>Paid listings coming soon</span>
            </div>
          </div>
          <div>
            <label style={s.label}>Accent colour</label>
            <div style={{ display:'flex', gap:7, marginTop:4 }}>
              {COVER_COLORS.map(c => (
                <button key={c} onClick={() => setV('cover_color', c)} style={{
                  width:22, height:22, borderRadius:'50%', background:c, border:'none', cursor:'pointer',
                  outline: form.cover_color === c ? `3px solid ${c}` : 'none', outlineOffset:2,
                  transform: form.cover_color === c ? 'scale(1.15)' : 'scale(1)', transition:'transform 0.1s',
                }} />
              ))}
            </div>
          </div>
          <div>
            <label style={s.label}>Cover image <span style={{ color:'var(--dim)', fontWeight:400 }}>(optional)</span></label>
            {coverUrl ? (
              <div style={{ position:'relative', borderRadius:8, overflow:'hidden', border:'0.5px solid var(--border2)' }}>
                <img src={coverUrl} alt="Cover" style={{ width:'100%', aspectRatio:'1/1', objectFit:'cover', display:'block' }} />
                <button onClick={() => setCoverUrl('')} style={{ position:'absolute', top:6, right:6, background:'rgba(0,0,0,0.6)', border:'none', borderRadius:'50%', width:22, height:22, cursor:'pointer', color:'#fff', fontSize:13 }}>×</button>
              </div>
            ) : (
              <label style={{ cursor: uploading ? 'wait' : 'pointer', display:'block' }}>
                <div style={{ border:'1.5px dashed var(--border2)', borderRadius:8, padding:'12px', textAlign:'center', background:'var(--bg)' }}>
                  <div style={{ fontSize:11, color:'var(--dim)' }}>{uploading ? 'Uploading…' : '🖼 Click to upload cover image'}</div>
                </div>
                <input type="file" accept="image/*" style={{ display:'none' }} onChange={handleImageUpload} disabled={uploading} />
              </label>
            )}
          </div>
          <div style={{ display:'flex', gap:8, paddingTop:4 }}>
            <button onClick={onClose} style={s.cancelBtn}>Cancel</button>
            <button onClick={handleSave} disabled={saving || !form.title.trim() || !form.description.trim()} style={{ ...s.saveBtn, flex:2, opacity:(!form.title.trim()||!form.description.trim())?0.5:1 }}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PublishedRow({ listing, onEdit, onUnpublish }) {
  const color = listing.cover_color || '#7c6ffb';
  return (
    <div style={{ background:'var(--surface)', border:'0.5px solid var(--border)', borderRadius:12, overflow:'hidden' }}>
      <div style={{ height:3, background:`linear-gradient(90deg,${color},${color}55)` }} />
      <div style={{ padding:'12px 16px', display:'flex', gap:12, alignItems:'flex-start' }}>
        <div style={{ width:44, height:44, borderRadius:9, flexShrink:0, overflow:'hidden', background:`${color}18`, border:`0.5px solid ${color}40`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:20 }}>
          {listing.cover_image_url
            ? <img src={listing.cover_image_url} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
            : (listing.item_type === 'playbook' ? '📦' : '⚡')}
        </div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:13.5, fontWeight:600, color:'var(--text)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', marginBottom:3 }}>{listing.title}</div>
          {listing.description && (
            <div style={{ fontSize:11.5, color:'var(--dim)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', marginBottom:4 }}>{listing.description}</div>
          )}
          <div style={{ display:'flex', gap:10, fontSize:11, color:'var(--dim)', flexWrap:'wrap' }}>
            <span>⬇ {listing.install_count || 0} installs</span>
            {listing.rating_count > 0 && <span>⭐ {Number(listing.avg_rating).toFixed(1)} ({listing.rating_count})</span>}
            {(listing.tags || []).slice(0,3).map(t => (
              <span key={t} style={{ padding:'1px 5px', borderRadius:4, background:'var(--surface2)', border:'0.5px solid var(--border)' }}>{t}</span>
            ))}
          </div>
        </div>
        <div style={{ display:'flex', flexDirection:'column', gap:5, flexShrink:0, alignItems:'flex-end' }}>
          <span style={{ fontSize:10, fontWeight:700, padding:'2px 8px', borderRadius:6, background:'rgba(29,158,117,0.1)', color:'#1d9e75', border:'0.5px solid rgba(29,158,117,0.3)' }}>
            {(listing.price_cents||0)>0 ? `$${(listing.price_cents/100).toFixed(2)}` : 'FREE'}
          </span>
          <button onClick={() => onEdit(listing)} style={{ fontSize:11, padding:'4px 10px', background:'var(--accent-dim)', color:'var(--accent)', border:'0.5px solid var(--accent-border)', borderRadius:6, cursor:'pointer', fontFamily:'var(--font)', fontWeight:500 }}>
            ✎ Edit
          </button>
          <button onClick={() => onUnpublish(listing)} style={{ fontSize:10, color:'var(--dim)', background:'none', border:'0.5px solid var(--border2)', borderRadius:5, padding:'2px 8px', cursor:'pointer', fontFamily:'var(--font)' }}>
            Unpublish
          </button>
        </div>
      </div>
    </div>
  );
}

function UnlistedRow({ item, onPublish }) {
  return (
    <div style={{ background:'var(--surface)', border:'0.5px dashed var(--border2)', borderRadius:10, padding:'10px 14px', display:'flex', alignItems:'center', gap:12 }}>
      <span style={{ fontSize:18, flexShrink:0 }}>{item.item_type === 'playbook' ? '📦' : '⚡'}</span>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontSize:13, fontWeight:500, color:'var(--text)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{item.title}</div>
        <div style={{ fontSize:11, color:'var(--dim)', marginTop:1 }}>Public · not listed on marketplace</div>
      </div>
      <button onClick={() => onPublish(item)} style={{ fontSize:12, padding:'6px 13px', background:'var(--accent)', color:'#fff', border:'none', borderRadius:7, cursor:'pointer', fontFamily:'var(--font)', fontWeight:500, flexShrink:0 }}>
        🚀 Publish →
      </button>
    </div>
  );
}

export default function CreatorPage() {
  const { user, isPro, isAdmin } = useAuth();
  const navigate   = useNavigate();
  const isPro_     = isPro || isAdmin;
  const [activeTab, setActiveTab] = useState('published');
  const [listings,  setListings]  = useState({ published:[], unlisted:[] });
  const [loading,   setLoading]   = useState(true);
  const [editItem,  setEditItem]  = useState(null);

  const loadListings = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    setLoading(true);
    try {
      const { supabase } = await import('../lib/supabase');
      const [{ data: mlData }, { data: skills }, { data: packs }] = await Promise.all([
        supabase.from('marketplace_listings').select('*').eq('user_id', user.id).order('install_count', { ascending: false }),
        supabase.from('skills').select('id,title,description,category,is_public,use_count,forked_from,user_id').eq('user_id', user.id).eq('is_public', true).is('forked_from', null),
        supabase.from('skill_packs').select('id,title,description,category,is_public,install_count,cover_color,forked_from,user_id').eq('user_id', user.id).eq('is_public', true).is('forked_from', null),
      ]);
      const listingItemIds = new Set((mlData||[]).map(l => l.item_id));
      const published = (mlData||[]).map(l => ({ ...l, listing_id: l.id, install_count: l.install_count||0 }));
      const unlisted  = [
        ...(skills||[]).filter(s => !listingItemIds.has(s.id)).map(s => ({ ...s, item_type:'skill',    install_count: s.use_count||0 })),
        ...(packs ||[]).filter(p => !listingItemIds.has(p.id)).map(p => ({ ...p, item_type:'playbook', install_count: p.install_count||0 })),
      ];
      setListings({ published, unlisted });
    } catch(e) { console.error(e); }
    setLoading(false);
  }, [user]);

  useEffect(() => { loadListings(); }, [loadListings]);

  const published = listings.published || [];
  const unlisted  = listings.unlisted  || [];
  const totalInstalls = published.reduce((s,l) => s + (l.install_count||0), 0);
  const ratedItems    = published.filter(l => l.rating_count > 0);
  const avgRating     = ratedItems.length > 0
    ? ratedItems.reduce((s,l) => s + (l.avg_rating||0), 0) / ratedItems.length
    : null;

  const handleUnpublish = async (item) => {
    if (!window.confirm('Remove this listing from the Marketplace?')) return;
    const { supabase } = await import('../lib/supabase');
    await supabase.from('marketplace_listings').update({ status:'draft' }).eq('id', item.listing_id).eq('user_id', user.id);
    loadListings();
  };

  if (!user) {
    return (
      <div style={s.page}>
        <div style={{ padding:'32px 24px', background:'var(--surface)', borderRadius:16, border:'0.5px solid var(--border)', textAlign:'center', maxWidth:400, margin:'0 auto' }}>
          <div style={{ fontSize:36, marginBottom:12 }}>🚀</div>
          <div style={{ fontSize:16, fontWeight:600, color:'var(--text)', marginBottom:12 }}>Sign in to manage your listings</div>
          <Link to="/auth" style={{ display:'block', padding:'12px', background:'var(--accent)', color:'#fff', borderRadius:10, textDecoration:'none', fontFamily:'var(--font)', fontSize:14, fontWeight:600 }}>Sign in →</Link>
        </div>
      </div>
    );
  }

  return (
    <div style={s.page}>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:20, gap:12, flexWrap:'wrap' }}>
        <div>
          <h1 style={{ fontSize:'clamp(19px,4vw,24px)', fontWeight:700, color:'var(--text)', margin:0, letterSpacing:'-0.02em' }}>Creator Dashboard</h1>
          <p style={{ fontSize:13, color:'var(--muted)', margin:'3px 0 0' }}>Manage your marketplace listings</p>
        </div>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          {isPro_ && <NotificationBell />}
          <Link to="/marketplace" style={{ ...s.btnSecondary, textDecoration:'none', fontSize:12 }}>Browse →</Link>
          <button onClick={() => navigate('/skills')} style={{ ...s.btnSecondary, fontSize:12 }}>+ Publish new</button>
        </div>
      </div>

      {/* Stats */}
      {published.length > 0 && (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(110px,1fr))', gap:8, marginBottom:20 }}>
          <StatCard label="Published"  value={published.length}  icon="📋" />
          <StatCard label="Installs"   value={totalInstalls}      icon="⬇" color="#1d9e75" />
          {avgRating && <StatCard label="Avg rating" value={avgRating.toFixed(1)+'★'} icon="⭐" color="#f59e0b" />}
          {unlisted.length > 0 && <StatCard label="Unlisted" value={unlisted.length} icon="👁" color="var(--dim)" sub="public, not listed" />}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display:'flex', borderBottom:'0.5px solid var(--border)', marginBottom:16 }}>
        {[
          { id:'published', label:`Published (${published.length})` },
          { id:'unlisted',  label:`Unlisted (${unlisted.length})`  },
        ].map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
            padding:'8px 16px', background:'none', border:'none',
            borderBottom:`2px solid ${activeTab===t.id?'var(--accent)':'transparent'}`,
            fontFamily:'var(--font)', fontSize:13,
            fontWeight: activeTab===t.id ? 600 : 400,
            color: activeTab===t.id ? 'var(--accent)' : 'var(--muted)',
            cursor:'pointer', transition:'all 0.12s',
          }}>{t.label}</button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
          {[1,2,3].map(i => <div key={i} style={{ height:72, background:'var(--surface)', borderRadius:10 }} className="skeleton" />)}
        </div>
      ) : activeTab === 'published' ? (
        published.length === 0 ? (
          <div style={{ textAlign:'center', padding:'48px 20px', background:'var(--surface)', borderRadius:12, border:'1.5px dashed var(--border2)' }}>
            <div style={{ fontSize:36, marginBottom:10, opacity:0.4 }}>🚀</div>
            <div style={{ fontSize:15, color:'var(--muted)', marginBottom:8 }}>No listings yet</div>
            <div style={{ fontSize:13, color:'var(--dim)', marginBottom:16, lineHeight:1.6 }}>
              Go to My Skills or My Playbooks, open any public item, and press <strong>Publish →</strong> to list it.
            </div>
            <button onClick={() => navigate('/skills')} style={{ padding:'9px 20px', background:'var(--accent)', color:'#fff', border:'none', borderRadius:8, fontFamily:'var(--font)', fontSize:13, fontWeight:500, cursor:'pointer' }}>
              Go to My Skills →
            </button>
          </div>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {published.map(l => <PublishedRow key={l.id} listing={l} onEdit={setEditItem} onUnpublish={handleUnpublish} />)}
          </div>
        )
      ) : (
        unlisted.length === 0 ? (
          <div style={{ textAlign:'center', padding:'28px 20px', background:'var(--surface)', borderRadius:12, border:'0.5px solid var(--border)' }}>
            <div style={{ fontSize:13, color:'var(--muted)' }}>All your public items are listed. 🎉</div>
          </div>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            <div style={{ fontSize:12, color:'var(--dim)', padding:'0 2px', marginBottom:4 }}>
              These are public but not listed with a description and tags. Press 🚀 Publish to add them to the marketplace.
            </div>
            {unlisted.map(item => (
              <UnlistedRow key={item.id} item={item} onPublish={item => navigate(`/skills?tab=${item.item_type==='playbook'?'playbooks':'mine'}`)} />
            ))}
          </div>
        )
      )}

      {/* Free user upgrade */}
      {!isPro_ && (
        <div style={{ marginTop:24, padding:'14px 18px', background:'var(--accent-dim)', border:'0.5px solid var(--accent-border)', borderRadius:12, display:'flex', gap:14, flexWrap:'wrap', alignItems:'center' }}>
          <div style={{ flex:1, minWidth:180 }}>
            <div style={{ fontSize:13, fontWeight:600, color:'var(--accent)', marginBottom:2 }}>🚀 Publish to the Marketplace</div>
            <div style={{ fontSize:12, color:'var(--muted)' }}>Upgrade to Pro to publish skills and playbooks.</div>
          </div>
          <Link to="/premium" style={{ padding:'8px 16px', background:'var(--accent)', color:'#fff', borderRadius:8, textDecoration:'none', fontFamily:'var(--font)', fontSize:12, fontWeight:600, flexShrink:0 }}>Upgrade →</Link>
        </div>
      )}

      {editItem && (
        <EditListingModal
          listing={editItem}
          onClose={() => setEditItem(null)}
          onSaved={updated => {
            setListings(prev => ({ ...prev, published: prev.published.map(l => l.listing_id === updated.listing_id ? { ...l, ...updated } : l) }));
            setEditItem(null);
          }}
        />
      )}
    </div>
  );
}

const s = {
  page:        { background:'var(--bg)', padding:'clamp(14px,3vw,24px)', fontFamily:'var(--font)', minHeight:'100vh' },
  label:       { fontSize:11, textTransform:'uppercase', letterSpacing:'0.06em', color:'var(--dim)', marginBottom:5, display:'block', fontWeight:500 },
  input:       { width:'100%', background:'var(--bg)', border:'0.5px solid var(--border2)', borderRadius:8, padding:'9px 12px', color:'var(--text)', fontFamily:'var(--font)', fontSize:13, outline:'none', boxSizing:'border-box' },
  btnSecondary:{ background:'none', color:'var(--muted)', border:'0.5px solid var(--border2)', borderRadius:8, padding:'7px 14px', fontFamily:'var(--font)', fontSize:13, cursor:'pointer', display:'inline-flex', alignItems:'center' },
  cancelBtn:   { flex:1, padding:'10px', background:'none', border:'0.5px solid var(--border2)', borderRadius:8, fontFamily:'var(--font)', fontSize:13, color:'var(--muted)', cursor:'pointer' },
  saveBtn:     { flex:2, padding:'10px', background:'var(--accent)', color:'#fff', border:'none', borderRadius:8, fontFamily:'var(--font)', fontSize:13, fontWeight:500, cursor:'pointer' },
};
